/** tm/legal/LegalFeedCard.tsx — 목록 카드 (정부지원사업 카드와 같은 모양): 굵은 제목 1~2줄 · 회색 한 줄 · 오른쪽 위 배지
 *  안 읽은 새 글 표시는 한 가지: 제목 앞 작은 파란 점 + 굵은 제목 (NEW 글자 배지는 팝업에만). 오른쪽 위 배지는 법령 시행 D-n · 입법예고 의견제출 D-n / 마감 · 국회 법안 상태 · 국무회의 법령안의 현재 단계.
 *  같은 내용 묶음(dup_count)은 회색 줄에 "같은 회신 n건", 원문 일자 의심(date_note)은 날짜 옆에. 색은 정부지원사업 D-day 규칙(7일 이내 파랑·그 밖 회색, 의견제출 D-3 이내만 빨강). 타법개정·마감 예고는 회색 바탕·보통 굵기(투명도로 흐리게 하지 않음 — 대비 유지) */
import { Badge, C, R, T } from '../components/ui';
import { shortSource, shortDate, shortTime, effectiveLabel, lawKindLabel, noticeDday, billStatusColor, ddayTone, cabinetStatus, MUTED } from './legalTheme';
import { ItemTitle } from './legalUi';
import type { FeedRow } from './legalTypes';

interface Props { row: FeedRow; selected: boolean; onClick: () => void }

/** 회색 한 줄: 종류 · 기관 · 날짜 */
function subLine(r: FeedRow): string {
  if (r.kind === 'news') return ['뉴스', `${r.lead_source}${r.article_count > 1 ? ` 외 ${r.article_count - 1}곳` : ''}`, shortTime(r.last_published_at || r.sort_date) || '일자 미상'].join(' · ');
  if (r.kind === 'bill') return ['국회 법안', r.committee || '위원회 회부 전', r.proposer, r.propose_dt || r.sort_date ? shortDate(r.propose_dt || r.sort_date) : '일자 미상'].filter(Boolean).join(' · ');
  if (r.kind === 'notice') return ['입법예고', lawKindLabel(r.law_kind), r.ministry || r.lead_source, `${shortDate(r.start_date)}~${shortDate(r.end_date)}`].filter(Boolean).join(' · ');
  if (r.kind === 'cabinet') return [`국무회의 ${shortDate(r.cabinet_date || r.sort_date)}`, lawKindLabel(r.law_kind), r.ministry || r.lead_source].filter(Boolean).join(' · ');
  if (r.tab === 'law') {
    const kind = r.kind_label === '제정' ? '신설' : r.meta?.minor ? '타법개정' : r.kind_label || '개정';
    return [lawKindLabel(r.law_kind || r.meta?.lawType) || '법령', kind, `공포 ${shortDate(r.decided_at)}`].join(' · ');
  }
  const date = r.sort_date ? shortDate(r.sort_date) : r.decided_at ? shortDate(r.decided_at) : '일자 미상';
  const dup = r.dup_count ? `같은 ${r.group === '유권해석' || /해석/.test(r.source_label) ? '회신' : '내용'} ${r.dup_count + 1}건` : '';
  return [shortSource(r.source, r.source_label), dup, r.case_no, r.date_note ? `${date} (${r.date_note})` : date].filter(Boolean).join(' · ');
}

/** 오른쪽 위 배지 */
const B = (bg: string, fg: string, t: string) => <Badge bg={bg} color={fg} style={{ fontSize: T.sm }}>{t}</Badge>;
function TopBadge({ r }: { r: FeedRow }) {
  if (r.kind === 'bill') {
    const c = billStatusColor(r.status);
    return B(c.bg, c.fg, r.status || '계류');
  }
  if (r.kind === 'cabinet') { const c = cabinetStatus(r.status); return B(c.bg, c.fg, c.text); }
  if (r.kind === 'notice') {
    const d = noticeDday(r.days_left, r.end_date);
    if (d) { const c = d.open ? ddayTone(d.days, d.urgent) : { fg: MUTED, bg: C.bgAlt }; return B(c.bg, c.fg, d.open ? d.text.replace('의견제출 ', '') : '마감'); }
  }
  if (r.kind === 'legal' && r.tab === 'law') {
    const e = effectiveLabel(r.effective_at);
    if (e?.upcoming) { const c = ddayTone(e.days); return B(c.bg, c.fg, e.text); }
  }
  return null;
}

export default function LegalFeedCard({ row, selected, onClick }: Props) {
  const dim = (row.kind === 'legal' && !!row.meta?.minor) || (row.kind === 'notice' && noticeDday(row.days_left, row.end_date)?.open === false);
  const fresh = row.is_new && row.unread;
  return (
    <button type="button" className="lw-f lw-card" onClick={onClick} aria-current={selected ? 'true' : undefined} style={{
      display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: R.md, marginBottom: 6, cursor: 'pointer', fontFamily: 'inherit',
      background: selected ? C.accentLt : dim ? C.bgAlt : C.white, border: `1px solid ${selected ? C.accent : C.border}`, position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 3 }}>
        <div className="lw-clamp" style={{ flex: 1, minWidth: 0,  fontSize: 15, fontWeight: fresh ? 700 : dim ? 400 : 500, color: dim && !fresh ? C.sub : C.text, lineHeight: 1.45 }}>
          {fresh && <span aria-label="안 읽은 새 글" title="안 읽은 새 글" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: C.accent, marginRight: 6, verticalAlign: 2 }} />}
          <ItemTitle title={row.title} custom={row.kind === 'legal' ? row.custom_title : null} />
        </div>
        <span style={{ flexShrink: 0 }}><TopBadge r={row} /></span>
      </div>
      <div style={{ fontSize: T.md, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {subLine(row)}{row.kind === 'legal' && row.company_count > 0 ? ` · 업체 ${row.company_count}` : ''}{row.starred ? ' · ★' : ''}
      </div>
    </button>
  );
}

/** 불러오는 동안 보여줄 자리 카드 */
export function CardSkeleton() {
  const bar = (w: string, h = 10) => <div className="lw-skel" style={{ width: w, height: h, borderRadius: 4, marginBottom: 7 }} />;
  return (
    <div aria-hidden="true" style={{ marginBottom: 6, background: C.white, border: `1px solid ${C.border}`, borderRadius: R.md, padding: '12px 12px 6px' }}>
      {bar('85%', 13)}{bar('55%')}
    </div>
  );
}
